import { useEffect, useMemo, useState } from 'react';
import { useMatch } from 'react-router-dom';

/**
 * Shared "where am I" hook for the dashboard chrome (card 064, fixed
 * in card 068).
 *
 * Two-way context:
 *
 *   1. URL-derived — when the current route lives under
 *      `/projects/:slug/...`, the hook returns the active project's
 *      `{ slug, name }` so any nested component can build a
 *      project-scoped URL without prop-drilling the slug down through
 *      the page tree.
 *
 *   2. Sticky — when the user navigates from a project route to a
 *      personal page (e.g. `/my-keys`, `/profile`), we stash the last
 *      seen project slug + name in sessionStorage so the HeaderBar can
 *      surface a "Back to <project>" link until they explicitly leave
 *      the tenant or pick a different project.
 *
 * Card 068 — the sticky half is keyed BY TENANT.  Project ids never
 * cross tenant boundaries, so neither does the cache: the storage key
 * is `spotlight.lastProject.<tenantId>`, and callers pass the active
 * tenant id (or `null` when no tenant is resolved).  When tenant
 * resolves to `null` the hook short-circuits — no reads, no writes,
 * no leftover chip from a previous tenant.
 *
 * The sticky half is intentionally per-session (not localStorage) —
 * crossing tenants in a new tab shouldn't drag stale project context
 * along.  Reload-within-tab preserves it, which is the only case
 * users actually notice.
 *
 * Returns either `null` (no project in scope, no sticky context) OR
 * an object:
 *
 *   {
 *     slug,        // string — `/projects/:slug` segment, never empty.
 *     name,        // string — project name when we've resolved it,
 *                  // otherwise falls back to the slug so the chrome
 *                  // never renders a blank chip.
 *     isUrlActive, // true when the current URL is /projects/:slug/...,
 *                  // false when only the sticky half is in play.
 *   }
 */

const STORAGE_PREFIX = 'spotlight.lastProject.';

function storageKeyFor(tenantId) {
  if (!tenantId) return null;
  return `${STORAGE_PREFIX}${tenantId}`;
}

function readSticky(tenantId) {
  if (typeof window === 'undefined') return null;
  const key = storageKeyFor(tenantId);
  if (!key) return null;
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.slug !== 'string' || !parsed.slug) return null;
    return { slug: parsed.slug, name: parsed.name || parsed.slug };
  } catch {
    return null;
  }
}

function writeSticky(tenantId, value) {
  if (typeof window === 'undefined') return;
  const key = storageKeyFor(tenantId);
  if (!key) return;
  try {
    if (!value) {
      window.sessionStorage.removeItem(key);
      return;
    }
    window.sessionStorage.setItem(
      key,
      JSON.stringify({ slug: value.slug, name: value.name || value.slug }),
    );
  } catch {
    /* private mode / quota — sticky is a UX nicety, not load-bearing */
  }
}

/**
 * Used by ProjectDetail to push the project name into the sticky
 * cache the moment we resolve it.  Components downstream then read
 * it through useProjectContext() without re-fetching the project
 * list themselves.
 *
 * `tenantId` MUST be the active tenant id at the time of the write
 * (card 068).  A write with a null tenant is a no-op — we never
 * stash a project under an unresolved tenant.
 */
export function rememberProjectContext(tenantId, { slug, name } = {}) {
  if (!tenantId || !slug) return;
  writeSticky(tenantId, { slug, name: name || slug });
}

/**
 * Tenant switch / explicit "leave project" — clears the sticky half
 * for a given tenant.  No-op when tenant id is null (nothing to
 * clear; the per-tenant key didn't exist).
 */
export function clearProjectContext(tenantId) {
  if (!tenantId) return;
  writeSticky(tenantId, null);
}

/**
 * Card 070 — read the sticky slug for a tenant WITHOUT subscribing
 * (no React state, safe inside a `useEffect`).  Returns null when the
 * key isn't present or the tenant id is empty.  HeaderBar uses this
 * for the auto-restore-on-tenant-pick navigation.
 */
export function peekProjectContext(tenantId) {
  return readSticky(tenantId);
}

export function useProjectContext(tenantId) {
  const projectMatch = useMatch('/projects/:slug/*');
  const urlSlug = projectMatch?.params?.slug || null;

  // Sticky state is initialised lazily from sessionStorage so SSR /
  // first-render doesn't blow up under React 19 strict mode.  The
  // key includes the tenant id (card 068), so a tenant change kicks
  // the in-memory cache via the effect below.
  const [sticky, setSticky] = useState(() => readSticky(tenantId));

  // When the tenant id changes (including to null), reset the sticky
  // half to whatever the new key holds.  Without this, switching
  // tenants would leave the previous tenant's project chip dangling
  // in the HeaderBar until the next remount.
  useEffect(() => {
    setSticky(readSticky(tenantId));
  }, [tenantId]);

  // When the URL points at a project, the URL is the truth — refresh
  // sticky to match (without name yet; ProjectDetail will fill it in
  // once it resolves).  Only meaningful when we also have a tenant
  // to key the storage write against.
  useEffect(() => {
    if (!tenantId || !urlSlug) return;
    if (sticky?.slug === urlSlug) return;
    const next = { slug: urlSlug, name: urlSlug };
    writeSticky(tenantId, next);
    setSticky(next);
    // No-op when leaving — the explicit clearProjectContext() /
    // rememberProjectContext() helpers handle the lifecycle.  We
    // never auto-clear on URL change because the whole point of the
    // sticky half is to survive personal-page detours.
  }, [tenantId, urlSlug, sticky?.slug]);

  return useMemo(() => {
    // No tenant → no project context at all.  Card 068: project chip
    // must not survive a tenant unresolve.
    if (!tenantId) return null;
    if (urlSlug) {
      return {
        slug: urlSlug,
        name: sticky?.slug === urlSlug ? sticky.name : urlSlug,
        isUrlActive: true,
      };
    }
    if (sticky?.slug) {
      return {
        slug: sticky.slug,
        name: sticky.name || sticky.slug,
        isUrlActive: false,
      };
    }
    return null;
  }, [tenantId, urlSlug, sticky]);
}
